/**
 *  @class
 *  @function FacetsToggle
 */
class FacetsToggle {
  constructor() {
    const drawer = document.getElementById('Facet-Drawer');
    const button = document.getElementById('Facets-Toggle');
    if (!drawer || !button || button.dataset.facetsBound) return;
    button.dataset.facetsBound = 'true';
    button.addEventListener('click', (event) => {
      event.preventDefault();
      const form = drawer.querySelector('form[data-apply-on-submit]');
      if (form) {
        form.reset();
        form.querySelectorAll('price-slider').forEach(price => {
          const min = price.querySelector('.field__input_min');
          const max = price.querySelector('.field__input_max');
          price.querySelector('.price_slider').noUiSlider?.set([
            min.value || 0, max.value || price.slider.dataset.max,
          ]);
        });
      }
      document.body.classList.add('open-cc');
      drawer.classList.add('active');
      drawer.removeAttribute('inert');
      button.setAttribute('aria-expanded', 'true');
      drawer.querySelector('.side-panel-close')?.focus();
    });
    if (drawer.dataset.facetsBound) return;
    drawer.dataset.facetsBound = 'true';
    new MutationObserver(() => {
      if (drawer.classList.contains('active')) return;
      const trigger = document.getElementById('Facets-Toggle');
      trigger?.setAttribute('aria-expanded', 'false');
      trigger?.focus({ preventScroll: true });
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    drawer.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        FacetsToggle.close();
      } else if (event.target.matches('.side-panel-close') && ['Enter', ' '].includes(event.key)) {
        event.preventDefault();
        FacetsToggle.close();
      } else if (event.key === 'Tab') {
        const controls = Array.from(drawer.querySelectorAll('button, input, select, a[href], [tabindex="0"], summary'))
          .filter(node => !node.disabled && node.getClientRects().length);
        const target = event.shiftKey ? controls.at(-1) : controls[0];
        if (event.target === (event.shiftKey ? controls[0] : controls.at(-1))) {
          event.preventDefault();
          target?.focus();
        }
      }
    });
  }

  static close() {
    const drawer = document.getElementById('Facet-Drawer');
    drawer?.classList.remove('active');
    drawer?.setAttribute('inert', '');
    document.body.classList.remove('open-cc');
  }
}

/**
 *  @class
 *  @function SidebarToggle
 */
class SidebarToggle {
  constructor() {
    this.container = document.getElementById('Sidebar-Container');
    const button = document.getElementById('Sidebar-Toggle');

    // Add functionality to buttons
    if (button && this.container && !button.dataset.sidebarBound) {
      button.dataset.sidebarBound = 'true';
      const span = button.querySelector('span');

      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.container.classList.toggle('facets--sidebar-open');

        span.textContent = this.container.classList.contains('facets--sidebar-open') ? theme.strings.hideFilters : theme.strings.showFilters;
      });
    }
  }
}

/**
 *  @class
 *  @function FacetFiltersForm
 */
class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    this.addEventListener('click', event => {
      const button = event.target.closest('.thb-filter-content--button');
      if (button) this.onMore.call(button, event);
    });
    const form = this.querySelector('form');
    form.addEventListener('input', (event) => {
      this.enforceSingleColor(event.target);
      if (!form.hasAttribute('data-apply-on-submit')) this.debouncedOnSubmit(event);
    });
    form.addEventListener('submit', event => {
      this.onSubmitHandler(event);
      if (form.hasAttribute('data-apply-on-submit')) FacetsToggle.close();
    });
  }

  connectedCallback() {
    if (!this.classList.contains('facets--bar')) return;
    this.updateStickyOffset = this.updateStickyOffset.bind(this);
    this.stickyHeaders = Array.from(document.querySelectorAll('.shopify-section-group-header-group, theme-header'));
    this.stickyResizeObserver = new ResizeObserver(this.updateStickyOffset);
    this.stickyHeaders.forEach(header => this.stickyResizeObserver.observe(header));
    window.addEventListener('resize', this.updateStickyOffset);
    this.updateStickyOffset();
  }

  disconnectedCallback() {
    this.stickyResizeObserver?.disconnect();
    if (this.updateStickyOffset) window.removeEventListener('resize', this.updateStickyOffset);
  }

  updateStickyOffset() {
    const offset = this.stickyHeaders.reduce((bottom, header) => {
      const style = getComputedStyle(header);
      const top = parseFloat(style.top);
      if (!['sticky', 'fixed'].includes(style.position) || !Number.isFinite(top) || !header.getClientRects().length) return bottom;
      return Math.max(bottom, top + header.getBoundingClientRect().height);
    }, 0);
    this.style.setProperty('--facets-sticky-offset', `${offset}px`);
  }

  enforceSingleColor(input) {
    const presenter = window.BloomrVariantPresentation;
    if (!presenter?.isColorFilter(input.name) || !input.checked) return;
    this.querySelectorAll('input').forEach((other) => {
      if (other !== input && presenter.isColorFilter(other.name)) other.checked = false;
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = window.location.search.slice(1);
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
    const params = new URLSearchParams(window.location.search);
    const single = window.BloomrVariantPresentation?.singleColorParams(params);
    if (single && single.toString() !== params.toString()) {
      FacetFiltersForm.renderPage(params.toString(), null, false);
    }
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    const single = window.BloomrVariantPresentation?.singleColorParams(searchParams).toString();
    const normalizeURL = single !== undefined && single !== new URLSearchParams(searchParams).toString();
    searchParams = single ?? searchParams;
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const requestId = ++FacetFiltersForm.requestId;

    if (FacetFiltersForm.abortController) {
      FacetFiltersForm.abortController.abort();
    }
    FacetFiltersForm.abortController = new AbortController();
    FacetFiltersForm.setLoading(true);

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, requestId);
      } else {
        FacetFiltersForm.renderSectionFromFetch(url, event, requestId);
      }
    });
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    else if (normalizeURL) history.replaceState({ searchParams }, '', `${window.location.pathname}?${searchParams}${window.location.hash || ''}`);

  }

  static renderSectionFromFetch(url, event, requestId) {
    fetch(url, {
      signal: FacetFiltersForm.abortController.signal,
      headers: { Accept: 'text/html' }
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Facet request failed with status ${response.status}`);
        }
        return response.text();
      })
      .then((responseText) => {
        if (requestId !== FacetFiltersForm.requestId) return;

        const html = responseText;
        if (!FacetFiltersForm.renderProductGridContainer(html, event)) {
          throw new Error('Facet response did not contain a usable product grid');
        }

        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductCount(html);

        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, {
          html,
          url
        }];

      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Error loading collection filters:', error);
        }
      })
      .finally(() => {
        if (requestId === FacetFiltersForm.requestId) {
          FacetFiltersForm.setLoading(false);
          FacetFiltersForm.abortController = null;
        }
      });
  }

  static renderSectionFromCache(filterDataUrl, event, requestId) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    try {
      if (!FacetFiltersForm.renderProductGridContainer(html, event)) {
        throw new Error('Cached facet response did not contain a usable product grid');
      }
      FacetFiltersForm.renderFilters(html, event);
      FacetFiltersForm.renderProductCount(html);

    } catch (error) {
      console.error('Error rendering cached collection filters:', error);
    } finally {
      if (requestId === FacetFiltersForm.requestId) {
        FacetFiltersForm.setLoading(false);
        FacetFiltersForm.abortController = null;
      }
    }
  }

  static renderProductGridContainer(html, event) {
    const currentGrid = document.getElementById('ProductGridContainer');
    const incomingGrid = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer');

    if (!currentGrid || !incomingGrid) return false;

    currentGrid.innerHTML = incomingGrid.innerHTML;

    const headerHeight = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
      10
    ) || 0;
    const top = currentGrid.getBoundingClientRect().top + document.documentElement.scrollTop - headerHeight - 30;

    currentGrid.querySelectorAll('.lazyload').forEach((image) => {
      if (window.lazySizes) {
        window.lazySizes.loader.unveil(image);
      }
    });

    if (event) {
      window.scrollTo({
        top,
        left: 0,
        behavior: 'smooth'
      });
    }

    return true;
  }

  static setLoading(isLoading) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    const collection = productGridContainer && productGridContainer.querySelector('.collection');

    if (collection) {
      collection.classList.toggle('loading', isLoading);
    }

    document.querySelectorAll('.thb-filter-count, .mobile-filter-count').forEach((item) => {
      item.classList.toggle('loading', isLoading);
    });

    if (productGridContainer) productGridContainer.setAttribute('aria-busy', String(isLoading));

    if (!isLoading) {
      FacetFiltersForm.toggleActiveFacets(false);
    }
  }

  static renderProductCount(html) {
    const countHtml = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
    const container = document.getElementsByClassName('thb-filter-count');

    if (countHtml) {
      for (var item of container) {
        item.innerHTML = countHtml.innerHTML;
        item.classList.remove('loading');
      }
    }

  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const incomingMobileForm = parsedHTML.getElementById('FacetFiltersFormMobile');
    const currentMobileForm = document.getElementById('FacetFiltersFormMobile');
    if (incomingMobileForm && currentMobileForm) {
      currentMobileForm.innerHTML = incomingMobileForm.innerHTML;
    }
    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    new FacetsToggle();
    new SidebarToggle();
    if (event?.target.matches('form[data-apply-on-submit]')) {
      document.getElementById('Facets-Toggle')?.focus({ preventScroll: true });
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelectorAll(selector).forEach(element => {
        element.innerHTML = activeFacetsElement.innerHTML;
      });
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-filter-count'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      const currentElement = document.querySelector(selector);
      if (!currentElement) return;
      currentElement.innerHTML = html.querySelector(selector).innerHTML;
      currentElement.classList.remove('loading');
    });

  }

  static updateURLHash(searchParams) {
    history.pushState({
      searchParams
    }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid) return [];

    return [
      {
        section: productGrid.dataset.id,
      }];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const formData = new FormData(event.target.closest('form'));
    const searchParams = window.BloomrVariantPresentation?.singleColorParams(
      new URLSearchParams(formData), event.target,
    ) || new URLSearchParams(formData);
    searchParams.delete('page');
    const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (preview) searchParams.set('preview_theme_id', preview);
    if (searchParams.get('filter.v.price.gte') === "0.00") {
      searchParams.delete('filter.v.price.gte');
    }
    const slider = event.target.closest('form').querySelector('.price_slider');
    if (slider) {
      if (searchParams.get('filter.v.price.lte') === parseFloat(slider.dataset.max).toFixed(2)) {
        searchParams.delete('filter.v.price.lte');
      }
    }
    FacetFiltersForm.renderPage(searchParams.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const nextParams = new URL(event.currentTarget.href).searchParams;
    const params = window.BloomrVariantPresentation?.singleColorParams(nextParams) || nextParams;
    params.delete('page');
    FacetFiltersForm.renderPage(params.toString(), event);
  }

  onMore(e) {
    let ul = this.closest('.thb-filter-content').querySelector('ul');

    if (ul.hasAttribute('filters-open')) {
      this.textContent = theme.strings.showMore;
      ul.removeAttribute('filters-open');
    } else {
      this.textContent = theme.strings.showLess;
      ul.setAttribute('filters-open', '');
    }
    e.preventDefault();
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.abortController = null;
FacetFiltersForm.requestId = 0;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

/**
 *  @class
 *  @function FacetRemove
 */
class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('a').forEach((item) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();
        const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
        form.onActiveFilterClick(event);
      });
    });
  }
}

customElements.define('facet-remove', FacetRemove);


/**
 *  @class
 *  @function FacetToolbar
 */
class FacetToolbar extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.productGrid = this.closest('#ProductGridContainer');
    this.compareToggle = this.querySelector('#compare_toggle');
    this.layoutSwitcher = this.querySelector('.facets--bar-view-as');
    this.buttons = this.querySelectorAll('.facets--bar-view-as-button');
    this.productLayout = window.localStorage.getItem('product-layout');
    if (this.layoutSwitcher) {
      this.buttons.forEach((button) => {
        button.addEventListener('click', (evt) => {
          [].forEach.call(this.buttons, function (el) {
            el.removeAttribute('disabled');
          });
          this.onLayoutChange(button, button.dataset.value);
          evt.preventDefault();
        });
      });

      if (this.productLayout && this.productLayout === 'list') {
        const toggle = this.querySelector(`.facets--bar-view-as-button-${this.productLayout}`);
        if (toggle) toggle.click();
      }
    }
    if (this.compareToggle) {
      this.compareToggle.addEventListener('change', this.onCompare);
    }
  }
  onLayoutChange(button, value) {
    button.setAttribute('disabled', '');
    this.productGrid.dataset.layout = value;
    window.localStorage.setItem('product-layout', value);
  }
  onCompare(e) {
    document.body.classList.toggle('compare-true');
    e.preventDefault();
  }
}
customElements.define('facet-toolbar', FacetToolbar);

/**
 *  @class
 *  @function PriceSlider
 */
class PriceSlider extends HTMLElement {

  constructor() {
    super();
  }
  connectedCallback() {
    this.slider = this.querySelector('.price_slider');
    this.amounts = this.querySelector('.price_slider_amount');
    this.form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    this.inputEvent = new CustomEvent('input');

    const args = {
      start: [
        parseFloat(this.slider.dataset.minValue || 0),
        parseFloat(this.slider.dataset.maxValue || this.slider.dataset.max)
      ],
      connect: true,
      step: 10,
      direction: document.dir,
      range: {
        'min': 0,
        'max': parseFloat(this.slider.dataset.max)
      }
    };
    noUiSlider.create(this.slider, args);

    this.slider.noUiSlider.on('update', this.onSliderUpdate.bind(this));
    this.slider.noUiSlider.on('change', this.onSliderChange.bind(this));

  }
  onSliderUpdate(values) {
    this.amounts.querySelector('.field__input_min').value = values[0];
    this.amounts.querySelector('.field__input_max').value = values[1];
  }

  onSliderChange(values) {
    this.form.querySelector('form').dispatchEvent(this.inputEvent);
  }
}
customElements.define('price-slider', PriceSlider);

new FacetsToggle();
new SidebarToggle();
